import { Fragment } from 'react';
import type { ReactElement } from 'react';
import type { FieldProps } from '@rjsf/utils';

// Renders an array of strings or flat objects as hidden inputs — no visible UI.
// Use in uiSchema as `"ui:field": "hiddenArray"`.
// Falls back to the schema `default` array when formData is absent so that
// the hidden values are present in the DOM even before a first interaction.
//
// String item:  one <input type="hidden"> per element.
// Object item:  one <input type="hidden"> per top-level string property.
//               Nested objects are not supported.

type FlatObject = Record<string, string>;
type HiddenValue = string | FlatObject;

function isObject(v: HiddenValue): v is FlatObject {
  return typeof v === 'object' && v !== null;
}

export function HiddenArrayField({
  formData,
  schema,
  fieldPathId,
}: FieldProps<HiddenValue[]>): ReactElement {
  const fallbackValues = Array.isArray(schema.default) ? (schema.default as HiddenValue[]) : [];
  const values = Array.isArray(formData) ? formData : fallbackValues;

  return (
    <>
      {values.map((value, i) => {
        if (isObject(value)) {
          return (
            <Fragment key={i}>
              {Object.entries(value).map(([prop, val]) => (
                <input
                  key={prop}
                  type="hidden"
                  id={`${fieldPathId.path}_${i}_${prop}`}
                  name={`${fieldPathId.path}_${i}_${prop}`}
                  value={val}
                  readOnly
                />
              ))}
            </Fragment>
          );
        }

        return (
          <input
            key={i}
            type="hidden"
            id={`${fieldPathId.path}_${i}`}
            name={`${fieldPathId.path}_${i}`}
            value={value}
            readOnly
          />
        );
      })}
    </>
  );
}
